import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { BRAND } from "../data/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/experiences", label: "Experiences" },
  { to: "/about", label: "About" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  return (
    <header
      data-testid="primary-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent text-white"
          : "bg-background/80 backdrop-blur-xl border-b border-border text-foreground"
      }`}
    >
      <div className="container-luxe flex items-center justify-between h-[72px] md:h-[88px]">
        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 -ml-2"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu size={22} strokeWidth={1.25} />
        </button>

        {/* Left nav */}
        <nav className="hidden md:flex items-center gap-9 text-[12px] uppercase tracking-[0.22em] font-sans font-medium flex-1">
          {links.slice(0, 3).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative transition-colors duration-300 hover:text-[var(--gold)] ${
                  isActive ? "text-accent" : ""
                }`
              }
              style={{ ['--gold']: 'hsl(var(--accent))' }}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Brand */}
        <Link
          to="/"
          data-testid="brand-logo"
          className="font-serif text-[22px] md:text-[26px] tracking-[0.1em] leading-none flex flex-col items-center"
        >
          <span>VOYAGE</span>
          <span className="text-[9px] tracking-[0.5em] -mt-0.5 opacity-70">— LUXE —</span>
        </Link>

        {/* Right nav */}
        <nav className="hidden md:flex items-center gap-9 text-[12px] uppercase tracking-[0.22em] font-sans font-medium flex-1 justify-end">
          {links.slice(3).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `transition-colors duration-300 hover:text-accent ${isActive ? "text-accent" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <ThemeToggle />
          <a
            href={BRAND.phone_link}
            data-testid="nav-call-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-current hover:bg-accent hover:border-accent hover:text-accent-foreground transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={1.5} />
            Concierge
          </a>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] bg-background text-foreground transition-transform duration-500 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:hidden`}
      >
        <div className="container-luxe flex items-center justify-between h-[72px] border-b border-border">
          <span className="eyebrow">Menu</span>
          <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
            <X size={22} strokeWidth={1.25} />
          </button>
        </div>
        <div className="container-luxe py-12 flex flex-col gap-7 font-serif text-3xl">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-accent transition-colors" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          <a href={BRAND.phone_link} className="mt-8 eyebrow text-foreground/70">Call · {BRAND.phone}</a>
          <a href={`mailto:${BRAND.email}`} className="eyebrow text-foreground/70">{BRAND.email}</a>
        </div>
      </div>
    </header>
  );
}
